import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import { Wafer } from "./types";
import styles from "./style.module.scss";

interface WaferTableProps {
  wafers: Wafer[];
  /** Paths of the wafers currently ticked, a subset of `wafers`. */
  selectedPaths: string[];
  onChange: (paths: string[]) => void;
  /** Locks the checkboxes, e.g. while the scan result is being exported. */
  disabled?: boolean;
}

function formatScanStartTime(value: string | null): string {
  if (!value) return "-";
  // Naive timestamps from DataServer, rendered as given rather than shifted into the
  // browser's timezone - the same choice the parent table makes.
  return value.replace("T", " ").slice(0, 19);
}

const columns: ColumnsType<Wafer> = [
  { key: "wafer_id", title: "Wafer", dataIndex: "wafer_id", width: "30%" },
  {
    key: "scan_start",
    title: "Scanned",
    dataIndex: "scan_start",
    width: "30%",
    render: (value: string | null) => formatScanStartTime(value),
  },
  {
    key: "defects",
    title: "Defects",
    dataIndex: "defects",
    width: "20%",
    align: "right",
    render: (value: number) => value.toLocaleString(),
  },
  {
    key: "defects_after_verification",
    title: "After verification",
    dataIndex: "defects_after_verification",
    width: "20%",
    align: "right",
    render: (value: number) => value.toLocaleString(),
  },
];

/**
 * The wafers of one scan result, with a checkbox each.
 *
 * A nested table rather than antd tree data on the parent: tree data would make every
 * wafer a key of the parent's own selection, and it was exactly that conflation - the
 * row key doubling as the path submitted to DataServer - that sent the lot folder to
 * export in the first place. Here the two selections stay separate things.
 *
 * A plain antd Table rather than VLTable: this is a handful of rows inside a row, so it
 * wants no pagination, no URL page parameter and no scroll cap.
 */
export default function WaferTable({
  wafers,
  selectedPaths,
  onChange,
  disabled = false,
}: WaferTableProps) {
  return (
    <Table<Wafer>
      className={styles.waferTable}
      columns={columns}
      dataSource={wafers}
      rowKey="path"
      size="small"
      pagination={false}
      rowSelection={{
        selectedRowKeys: selectedPaths,
        onChange: (keys) => onChange(keys.map(String)),
        getCheckboxProps: () => ({ disabled }),
      }}
    />
  );
}
